import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { Eyebrow, Reveal, Section } from "@/components/Primitives";
import { CtaBanner, CaseCard } from "@/components/Blocks";
import { PLATFORMS, TESTIMONIALS, CLIENTS } from "@/lib/data";

function Marquee() {
    const items = [...PLATFORMS, ...PLATFORMS];
    return (
        <div
            className="relative overflow-hidden border-y border-pa-line py-6 bg-white"
            aria-label="Platforms and systems we integrate with"
        >
            <div className="flex w-max animate-marquee gap-10 whitespace-nowrap will-change-transform">
                {items.map((p, i) => (
                    <span
                        key={i}
                        className="font-mono text-sm uppercase tracking-widest text-pa-ink-500"
                    >
                        {p}
                        <span className="ml-10 text-pa-blue-600">/</span>
                    </span>
                ))}
            </div>
        </div>
    );
}

export default function ConversationalAI() {
    const convCases = CLIENTS.filter((c) =>
        [
            "eagle-hills",
            "titan",
            "manipal",
            "olive-embassy",
            "qatar-post",
            "college-ave",
        ].includes(c.id),
    );
    return (
        <>
            <Seo
                title="Conversational AI"
                description="3+ years and 20+ conversational AI deployments across healthcare, real estate, HR, retail, FMCG, and fintech — integrated deep into enterprise systems. The agent work stands on this record."
                path="/conversational-ai"
            />

            <Section className="pt-16 md:pt-24 pb-10">
                <Reveal>
                    <Eyebrow>Conversational AI · the track record</Eyebrow>
                </Reveal>
                <Reveal delay={0.05}>
                    <h1 className="mt-6 font-display font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-4xl">
                        The agent work stands on a delivery record, not a pitch
                        deck
                    </h1>
                </Reveal>
                <Reveal delay={0.1}>
                    <p className="mt-6 text-lg md:text-xl text-pa-ink-500 max-w-2xl leading-relaxed">
                        Before Phoebus AI built agents, it spent years shipping
                        conversational systems into live enterprise
                        environments. That is where the confidence to automate
                        real workflows comes from.
                    </p>
                </Reveal>

                <div className="mt-12 grid gap-6 sm:grid-cols-3 max-w-3xl">
                    {[
                        ["3+", "years shipping conversational AI"],
                        ["20+", "enterprise deployments"],
                        ["6", "sectors served"],
                    ].map(([n, label]) => (
                        <Reveal key={label}>
                            <div className="border-t-2 border-pa-blue-600 pt-4">
                                <div className="font-mono text-4xl md:text-5xl text-pa-blue-600">
                                    {n}
                                </div>
                                <div className="mt-2 text-pa-ink-500">
                                    {label}
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </Section>

            <Marquee />

            <Section>
                <Reveal>
                    <Eyebrow>Sectors</Eyebrow>
                    <p className="mt-4 font-display text-2xl md:text-3xl font-bold max-w-3xl leading-snug">
                        Healthcare, real estate, HR, retail, FMCG, and fintech —
                        integrated into SAP, Azure AD, Salesforce, MS Dynamics
                        CRM, Hospital Information Systems, Shopify, and WhatsApp
                        commerce.
                    </p>
                </Reveal>
            </Section>

            {/* Testimonials */}
            <Section className="bg-pa-ink-900 text-white" dark>
                <Reveal>
                    <Eyebrow light>In their words</Eyebrow>
                </Reveal>
                <div className="mt-10 grid gap-6 md:grid-cols-2">
                    {TESTIMONIALS.map((t, i) => (
                        <Reveal key={i} delay={i * 0.08}>
                            <figure className="h-full border border-white/10 p-8 cut-tr-lg bg-white/[0.03]">
                                <blockquote className="font-display text-xl md:text-2xl leading-snug">
                                    {t.quote}
                                </blockquote>
                                <figcaption className="mt-6 font-mono text-sm text-pa-blue-050/70">
                                    <span className="text-white">{t.name}</span>
                                    <br />
                                    {t.role}
                                </figcaption>
                            </figure>
                        </Reveal>
                    ))}
                </div>
            </Section>

            <Section>
                <Reveal>
                    <div className="flex flex-wrap items-end justify-between gap-4">
                        <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">
                            Representative deployments
                        </h2>
                        <Link
                            to="/work"
                            className="font-mono text-sm text-pa-blue-700 hover:text-pa-blue-600"
                        >
                            All case studies →
                        </Link>
                    </div>
                </Reveal>
                <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {convCases.map((c, i) => (
                        <CaseCard key={c.id} c={c} index={i} />
                    ))}
                </div>
            </Section>

            <CtaBanner />
        </>
    );
}
